import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const page: ContentPageData = {
  path: '/funktsii/ivr-menyu',
  parent: { name: 'Можливості', path: '/funktsii' },
  crumb: 'IVR-меню і сценарії',
  title: 'IVR-меню і сценарії дзвінків у конструкторі | Anytel',
  description:
    'Голосове меню, робочі години, переведення на операторів і AI-агент в одному сценарії. Візуальний конструктор IVR і готові шаблони без програмування.',
  eyebrow: 'Можливості',
  h1: 'IVR-меню і сценарії дзвінків',
  lead:
    'Сценарій вирішує, що відбувається з кожним дзвінком: хто відповідає, що звучить у вихідний, куди йде клієнт після «натисніть 2». В Anytel його збирають мишкою з готових кроків, а для старту є шаблони.',
  sections: [
    {
      h2: 'Що таке сценарій дзвінка',
      blocks: [
        'Це схема з кроків, через які проходить дзвінок. Наприклад: привітання, перевірка робочих годин, у робочий час дзвінок іде операторам, а в неробочий AI-агенту, який приймає заявку. Схему видно цілком на одному екрані, тож логіку легко пояснити колезі й так само легко змінити.',
      ],
    },
    {
      h2: 'З яких кроків складається',
      blocks: [
        {
          list: [
            'Привітання й повідомлення: текст, який озвучує синтез мовлення, або записаний аудіофайл.',
            'Голосове меню: «натисніть 1, щоб записатися, 2, щоб дізнатися статус замовлення».',
            'Збір цифр з клавіатури: номер замовлення, код клієнта, інша коротка відповідь.',
            'Перевірка робочих годин і вихідних.',
            'Переведення на співробітника, на зовнішній номер або в чергу операторів, які відповідають з браузера.',
            '[AI-агент](/funktsii/ai-agent-dlya-dzvinkiv) для вільної розмови.',
            'Запит у вашу систему і розгалуження за її відповіддю.',
            'Заявка, контакт, запис у календар, передзвін: дії, що лишають результат у кабінеті.',
          ],
        },
      ],
    },
    {
      h2: 'Готові шаблони',
      blocks: [
        'Щоб не починати з чистого аркуша, у кабінеті є шаблони сценаріїв:',
        {
          list: [
            '«AI-адміністратор 24/7»: агент відповідає на всі дзвінки.',
            '«Вдень оператор, вночі AI»: у робочі години дзвінки йдуть людям, після них агенту.',
            '«Голосове меню»: класичне IVR з вибором відділу.',
          ],
        },
        'Кожен шаблон починається з попередження про запис розмови і віртуального асистента, тож чесний запис дзвінків уже врахований.',
      ],
    },
    {
      h2: 'Приклади сценаріїв',
      blocks: [
        { h3: 'Клініка з двома філіями' },
        'Привітання й меню: «натисніть 1, якщо вам на Поділ, 2, якщо на Оболонь». Кожна гілка веде до свого AI-агента з базою знань і календарями своєї філії.',
        { h3: 'Сервіс з виїзними майстрами' },
        'У робочий час дзвінок іде в чергу операторів. Якщо ніхто не відповів за кілька гудків, дзвінок приймає агент і створює заявку з адресою та описом проблеми. Уночі дзвінок одразу йде агенту.',
        { h3: 'Магазин зі статусом замовлення' },
        '«Натисніть 1, щоб дізнатися статус замовлення». Клієнт вводить номер замовлення з клавіатури, сценарій питає вашу систему і озвучує відповідь. Усі інші питання бере на себе агент.',
      ],
    },
    {
      h2: 'Видно, як пройшов кожен дзвінок',
      blocks: [
        'Для кожного дзвінка кабінет показує хід сценарію: якими кроками він пройшов, що натиснув клієнт, що відповіла ваша система, чим закінчилась розмова з агентом. Якщо щось пішло не так, причину видно одразу, без розбору технічних логів.',
      ],
    },
  ],
  after: [
    {
      h2: 'Сценарій без програміста',
      blocks: [
        {
          steps: [
            { title: 'Оберіть шаблон', text: 'Почніть з найближчого до вашого бізнесу.' },
            { title: 'Змініть кроки мишкою', text: 'Перетягуйте кроки, з’єднуйте виходи, редагуйте тексти. Конструктор підсвічує проблеми ще до збереження.' },
            { title: 'Перевірте з браузера', text: 'Тестовий дзвінок проходить саме цим сценарієм: натискайте цифри, говоріть з агентом, дивіться хід сценарію.' },
          ],
        },
        'Готові схеми під вечори, вихідні й години пік описані на сторінці [«Робота 24/7»](/funktsii/robota-24-7).',
      ],
    },
  ],
  faq: [
    {
      q: 'Чим це відрізняється від звичайного IVR оператора зв’язку?',
      a: 'Класичне IVR вміє лише меню й переадресацію. Тут у тому ж сценарії працюють AI-агент, календар, заявки й запити у ваші системи, а кожен дзвінок записаний і видно, як він пройшов.',
    },
    {
      q: 'Чи можна мати різні сценарії для різних номерів?',
      a: 'Так. Кожному номеру чи SIP-адресі призначається свій сценарій: наприклад, окремий для рекламної кампанії або для філії.',
    },
    {
      q: 'Чи потрібен програміст?',
      a: 'Ні. Сценарій збирається мишкою з готових кроків. Розробник може знадобитися лише для інтеграції з вашою внутрішньою системою, і то з її боку.',
    },
    {
      q: 'Чи можна записати привітання своїм голосом?',
      a: 'Так, замість синтезованого тексту можна використати власний аудіофайл.',
    },
  ],
  related: ['/funktsii/ai-agent-dlya-dzvinkiv', '/funktsii/robota-24-7', '/funktsii/zapys-i-transkryptsiya-dzvinkiv', '/dlya/avtoservisu'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
